import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { TimeUnitForPages } from 'webapp/utils/goal-value-rows';
import { calculateChange, roundGoalValue } from 'webapp/utils/number';

import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

interface GoalValueRowData {
	id: string;
	title: string;
	periodStartTs: number;
	periodEndTs: number;
	timeUnit: TimeUnitForPages;
	pageId: string;
	value: number | null;
	isMetricPercentage: boolean;
	isMetricInverted: boolean;
}

export default class GoalValueRow {
	id: string;
	title: string;
	periodStartTs: number;
	periodEndTs: number;
	timeUnit: TimeUnitForPages;
	pageId: string;
	isActive: boolean;
	isPast: boolean;
	isMetricPercentage: boolean;
	isMetricInverted: boolean;

	@tracked previousPoint: DChartPrimaryPoint | null = null;

	@tracked isHidden = false; // when hiding the ones past end date

	@tracked previousRow: GoalValueRow | null = null;

	// the goal value
	@tracked valueOriginal: number | null = null;
	@tracked value: number | null = null;

	constructor(data: GoalValueRowData, currentMomentRef = moment()) {
		// BE returns first timestamp of next period, we need non-overlapping
		const periodEndTsNonOverlapping = data.periodEndTs - 1;

		this.id = data.id;
		this.title = data.title;
		this.periodStartTs = data.periodStartTs;
		this.periodEndTs = periodEndTsNonOverlapping;
		this.timeUnit = data.timeUnit;
		this.pageId = data.pageId;

		this.valueOriginal = data.value;
		this.updateValue(data.value);

		const now = currentMomentRef.unix();
		this.isActive = now >= data.periodStartTs && now <= periodEndTsNonOverlapping;
		this.isPast = periodEndTsNonOverlapping < now;

		this.isMetricPercentage = data.isMetricPercentage;
		this.isMetricInverted = data.isMetricInverted;
	}

	get isDirty() {
		return this.valueOriginal !== this.value;
	}

	get previousRowValue() {
		return this.previousRow?.value ?? null;
	}

	get shownValue(): string {
		if (this.hasValue) {
			const roundedValue = roundGoalValue(
				this.value,
				this.isMetricPercentage,
				this.isMetricInverted,
			);
			return String(roundedValue);
		} else {
			return '';
		}
	}

	get hasValue() {
		return typeof this.value === 'number';
	}

	get percentFromPreviousPointValue() {
		if (this.previousPoint?.hasValue && this.hasValue) {
			const previousValue = this.previousPoint.value!;
			return this.getPercent(previousValue);
		} else {
			return null;
		}
	}

	get percentFromPreviousRowValue() {
		if (this.previousRow?.hasValue && this.hasValue) {
			const previousValue = this.previousRowValue!;
			return this.getPercent(previousValue);
		} else {
			return null;
		}
	}

	get shownPercentFromPreviousPointValue() {
		return this.getPercentString(this.percentFromPreviousPointValue);
	}

	get shownPercentFromPreviousRowValue() {
		return this.getPercentString(this.percentFromPreviousRowValue);
	}

	updateValue(value: number | null) {
		this.value = value;
	}

	updatePercent(percent: number) {
		if (this.previousRow?.hasValue && typeof percent === 'number') {
			const previousValue = this.previousRowValue!;
			const value = previousValue + previousValue * (percent / 100);
			this.updateValue(value);
		} else {
			this.updateValue(null);
		}
	}

	markClean() {
		this.valueOriginal = this.value;
	}

	private getPercent(previousValue: number) {
		const percent = calculateChange(previousValue, this.value);
		return roundGoalValue(percent || 0, this.isMetricPercentage, this.isMetricPercentage);
	}

	private getPercentString(percent: number | null): string {
		if (percent === null) {
			return '';
		}
		return `${percent}%`;
	}
}
